import { useEffect, useState } from 'react';
import { exchangeLtiCode } from '@/api/lti';

interface LtiAuthState {
  isExchanging: boolean;
  error: string | null;
  done: boolean;
}

/**
 * Detects an `lti_code` query parameter in the URL, exchanges it for real
 * tokens via POST /lti/exchange, stores them in localStorage, and removes
 * the one-time code from the URL so it is never visible in history.
 *
 * Usage: call this hook once at the top level of the LTI landing route.
 * After `done === true` the normal `useAuth` hook will pick up the tokens.
 */
export function useLtiAuth(): LtiAuthState {
  const [state, setState] = useState<LtiAuthState>({
    isExchanging: false,
    error: null,
    done: false,
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('lti_code');

    if (!code) {
      setState({ isExchanging: false, error: null, done: true });
      return;
    }

    setState({ isExchanging: true, error: null, done: false });

    exchangeLtiCode(code)
      .then((tokens) => {
        localStorage.setItem('access_token', tokens.access_token);
        localStorage.setItem('refresh_token', tokens.refresh_token);

        // Remove lti_code from the URL without a page reload
        params.delete('lti_code');
        const newSearch = params.toString();
        const newUrl = newSearch
          ? `${window.location.pathname}?${newSearch}`
          : window.location.pathname;
        window.history.replaceState({}, '', newUrl);

        setState({ isExchanging: false, error: null, done: true });
      })
      .catch((err: unknown) => {
        const message =
          err instanceof Error ? err.message : 'LTI token exchange failed';
        setState({ isExchanging: false, error: message, done: false });
      });
  }, []);

  return state;
}
